export function isPIIScrubDisabled(): boolean {
  return process.env.SENTRY_DISABLE_PII_SCRUB === "true";
}

const SAFE_KEYS = new Set([
  "tokenurl",
  "token_url",
  "tokentype",
  "token_type",
  "tokenendpoint",
  "token_endpoint",
  "tokencount",
  "token_count",
  "tokenexpiry",
  "token_expiry",
  "tokenttl",
  "token_ttl",
  "hasrefreshtoken",
  "has_refresh_token",
  "hasaccesstoken",
  "has_access_token",
  "authprovider",
  "auth_provider",
  "authevent",
  "auth_event",
  "authoperation",
  "auth_operation",
  "authmethod",
  "auth_method",
  "authtype",
  "auth_type",
  "authflow",
  "auth_flow",
  "authstep",
  "auth_step",
  "authphase",
  "auth_phase",
  "secrettype",
  "secret_type",
  "secretname",
  "secret_name",
  "addresstype",
  "address_type",
  "discoveryurl",
  "discovery_url",
  "developernote",
  "developer_note",
  "statedescription",
  "state_description",
  "errorstate",
  "error_state",
  "sessionstate",
  "session_state",
  "connectionstate",
  "connection_state",
  "actionname",
  "action_name",
  "functionname",
  "function_name",
  "operationname",
  "operation_name",
  "phonetype",
  "phone_type",
]);

const EXACT_SENSITIVE_KEYS = new Set([
  "password",
  "token",
  "secret",
  "authorization",
  "credential",
  "credentials",
  "ssn",
  "cvv",
  "pin",
  "apikey",
  "api_key",
  "email",
  "phone",
  "address",
  "income",
  "dob",
  "dateofbirth",
  "date_of_birth",
  "passwordhash",
  "password_hash",
  "codeverifier",
  "code_verifier",
  "codechallenge",
  "code_challenge",
  "recoverycode",
  "recovery_code",
]);

const SENSITIVE_SUFFIXES = [
  "password", "_password",
  "token", "_token",
  "secret", "_secret",
  "_key",
];

const SUBSTRING_PII_PATTERNS = [
  "creditcard", "credit_card",
  "cardnumber", "card_number",
  "socialsecurity", "social_security",
  "taxid", "tax_id",
];

const EMAIL_EXACT_KEYS = new Set(["to", "from"]);

export function isSensitiveKey(key: string): boolean {
  const lowerKey = key.toLowerCase();

  if (SAFE_KEYS.has(lowerKey)) return false;

  if (EXACT_SENSITIVE_KEYS.has(lowerKey) || EMAIL_EXACT_KEYS.has(lowerKey)) return true;

  if (lowerKey.length > 3) {
    for (const suffix of SENSITIVE_SUFFIXES) {
      if (lowerKey !== suffix && lowerKey.endsWith(suffix)) return true;
    }
  }

  for (const pattern of SUBSTRING_PII_PATTERNS) {
    if (lowerKey.includes(pattern)) return true;
  }

  return false;
}

export function scrubPIIFromEvent(
  obj: Record<string, unknown>
): Record<string, unknown> {
  if (isPIIScrubDisabled()) return obj;

  const scrubbed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (isSensitiveKey(key)) {
      scrubbed[key] = "[REDACTED]";
    } else if (Array.isArray(value)) {
      scrubbed[key] = value.map((item) =>
        item && typeof item === "object"
          ? scrubPIIFromEvent(item as Record<string, unknown>)
          : typeof item === "string" ? scrubPIIFromString(item) : item
      );
    } else if (value && typeof value === "object") {
      scrubbed[key] = scrubPIIFromEvent(value as Record<string, unknown>);
    } else if (typeof value === "string") {
      scrubbed[key] = scrubPIIFromString(value);
    } else {
      scrubbed[key] = value;
    }
  }

  return scrubbed;
}

export function scrubEmailsFromString(str: string): string {
  if (isPIIScrubDisabled()) return str;
  return str.replace(
    /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
    "[EMAIL_REDACTED]"
  );
}

const PHONE_REGEX = /(?<!\d)(?:\+\d{1,3}[-.\s]?)?\(?\d{3}\)[-.\s]?\d{3}[-.\s]?\d{4}(?!\d)|(?<!\d)\+\d{1,3}[-.\s]?\d{3}[-.\s]?\d{3}[-.\s]?\d{4}(?!\d)|(?<!\d)\d{3}[-.]\d{3}[-.]\d{4}(?!\d)/g;
const SSN_REGEX = /\b\d{3}-\d{2}-\d{4}\b/g;
const CREDIT_CARD_REGEX = /\b(?:\d[ -]*?){13,19}\b/g;

export function scrubPIIFromString(str: string): string {
  if (isPIIScrubDisabled()) return str;
  let result = scrubEmailsFromString(str);
  result = result.replace(SSN_REGEX, "[SSN_REDACTED]");
  result = result.replace(CREDIT_CARD_REGEX, "[CARD_REDACTED]");
  result = result.replace(PHONE_REGEX, "[PHONE_REDACTED]");
  return result;
}
